import { useEffect, useState } from "react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import {
  X, Copy, Check, ChartBar, MouseSimple, GlobeHemisphereWest, Broadcast,
  Tag, EnvelopeSimple, MagnifyingGlass, Chats,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";

const ICON = {
  facebook: <FaFacebook color="#1877F2" size={14} />,
  instagram: <FaInstagram color="#E4405F" size={14} />,
  linkedin: <FaLinkedin color="#0A66C2" size={14} />,
};

const REF_ICON = (name) => {
  const n = (name || "").toLowerCase();
  if (n === "facebook") return <FaFacebook color="#1877F2" size={12} />;
  if (n === "linkedin") return <FaLinkedin color="#0A66C2" size={12} />;
  if (n === "instagram") return <FaInstagram color="#E4405F" size={12} />;
  if (n === "email") return <EnvelopeSimple size={12} />;
  if (n === "google") return <MagnifyingGlass size={12} />;
  if (n === "direct") return <Broadcast size={12} />;
  if (n === "whatsapp" || n === "telegram") return <Chats size={12} />;
  return <Tag size={12} />;
};

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  return cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

export default function AnalyticsPanel({ post, onClose }) {
  const [data, setData] = useState(null);
  const [copiedCode, setCopiedCode] = useState("");

  useEffect(() => {
    if (!post) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/posts/${post.id}/analytics`);
        if (mounted) setData(data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { mounted = false; clearInterval(t); };
  }, [post]);

  if (!post) return null;

  const copy = async (platform, url) => {
    await navigator.clipboard.writeText(url);
    setCopiedCode(platform);
    setTimeout(() => setCopiedCode(""), 1500);
    toast.success("Short link copied");
  };

  const max = Math.max(1, ...Object.values(data?.per_platform || {}));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      <div
        data-testid={TID.analyticsPanel}
        className="w-full max-w-md bg-[#0f0f10] border-l border-[#27272A] h-full overflow-auto"
      >
        <div className="sticky top-0 bg-[#0f0f10]/95 backdrop-blur-xl border-b border-[#27272A] p-5 flex items-start justify-between">
          <div>
            <div className="overline flex items-center gap-2"><ChartBar size={12} /> post analytics</div>
            <div className="font-display text-2xl mt-1">Clicks & reach</div>
            <div className="text-xs text-[#A1A1AA] mt-1 font-mono truncate max-w-[280px]">{post.url}</div>
          </div>
          <button
            data-testid={TID.analyticsCloseBtn}
            onClick={onClose}
            className="p-1.5 text-[#71717A] hover:text-white hover:bg-[#27272A]"
            style={{ borderRadius: 2 }}
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5">
          <div className="border border-[#27272A] p-5 mb-5" style={{ borderRadius: 2 }}>
            <div className="overline">total clicks</div>
            <div className="font-display text-5xl mt-1 text-[#D4FF00] flex items-baseline gap-3">
              {data?.total_clicks ?? 0}
              <span className="text-xs text-[#71717A] font-mono uppercase tracking-widest">tracked</span>
            </div>
          </div>

          <div className="overline mb-3">by platform</div>
          <div className="flex flex-col gap-3 mb-6">
            {(post.platforms || []).map((p) => {
              const n = data?.per_platform?.[p] ?? 0;
              const w = Math.max(4, Math.round((n / max) * 100));
              return (
                <div key={p} className="flex items-center gap-3">
                  <div className="w-24 flex items-center gap-2 text-xs capitalize">
                    {ICON[p]} {p}
                  </div>
                  <div className="flex-1 h-6 bg-[#18181B] border border-[#27272A] relative" style={{ borderRadius: 2 }}>
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${w}%`, background: n > 0 ? "#FF4500" : "transparent" }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-white">{n}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="overline mb-2">short links <span className="normal-case text-[9px] tracking-normal text-[#71717A]">(with auto UTM tags)</span></div>
          <div className="flex flex-col gap-2 mb-6">
            {Object.entries(data?.short_links || {}).map(([p, url]) => (
              <div key={p} className="flex items-center gap-2 border border-[#27272A] p-2" style={{ borderRadius: 2 }}>
                <div className="w-5">{ICON[p]}</div>
                <div className="text-[11px] font-mono text-[#A1A1AA] truncate flex-1">{url}</div>
                <button
                  onClick={() => copy(p, url)}
                  className="p-1.5 text-[#71717A] hover:text-[#D4FF00]"
                  title="Copy short link"
                >
                  {copiedCode === p ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                </button>
              </div>
            ))}
            {data?.campaign && (
              <div className="text-[10px] font-mono text-[#71717A] px-1">
                utm_campaign = <span className="text-[#D4FF00]">{data.campaign}</span> · utm_medium = <span className="text-[#D4FF00]">social</span>
              </div>
            )}
          </div>

          <div className="overline mb-3 flex items-center gap-2"><Tag size={11} /> referrer breakdown</div>
          <div data-testid="analytics-referrers" className="flex flex-col gap-2 mb-6">
            {Object.keys(data?.per_referrer || {}).length === 0 ? (
              <div className="text-xs text-[#71717A]">— no click data yet —</div>
            ) : (
              Object.entries(data.per_referrer).sort((a, b) => b[1] - a[1]).map(([name, n]) => {
                const w = Math.max(4, Math.round((n / Math.max(1, ...Object.values(data.per_referrer))) * 100));
                return (
                  <div key={name} className="flex items-center gap-3">
                    <div className="w-24 flex items-center gap-2 text-xs capitalize">
                      {REF_ICON(name)} <span className="truncate">{name}</span>
                    </div>
                    <div className="flex-1 h-6 bg-[#18181B] border border-[#27272A] relative" style={{ borderRadius: 2 }}>
                      <div className="h-full transition-all duration-500" style={{ width: `${w}%`, background: "#D4FF00", opacity: 0.7 }} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-white">{n}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="overline mb-3 flex items-center gap-2"><GlobeHemisphereWest size={11} /> geography</div>
          <div data-testid="analytics-countries" className="flex flex-col gap-1 mb-6 border border-[#27272A]" style={{ borderRadius: 2 }}>
            {(data?.per_country || []).length === 0 ? (
              <div className="text-xs text-[#71717A] p-3">— no geo data yet —</div>
            ) : (
              (data.per_country || []).map((row, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] last:border-b-0 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-lg leading-none">{flagEmoji(row.code)}</span>
                    <span className="truncate">{row.country}</span>
                    {row.code && <span className="font-mono text-[10px] text-[#71717A]">{row.code}</span>}
                  </div>
                  <span className="font-mono text-xs text-[#D4FF00]">{row.n}</span>
                </div>
              ))
            )}
          </div>

          <div className="overline mb-2">recent clicks</div>
          {(data?.recent_clicks || []).length === 0 ? (
            <div className="border border-dashed border-[#27272A] p-6 text-center text-xs text-[#71717A]" style={{ borderRadius: 2 }}>
              <MouseSimple size={20} className="mx-auto mb-2" />
              No clicks yet — share the post and watch this update live.
            </div>
          ) : (
            <div className="border border-[#27272A] divide-y divide-[#27272A]" style={{ borderRadius: 2 }}>
              {data.recent_clicks.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-xs gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {ICON[c.platform] || null}
                    <span className="capitalize truncate">{c.platform}</span>
                    <span className="text-[#3F3F46]">←</span>
                    <span className="flex items-center gap-1 text-[#A1A1AA] truncate">
                      {REF_ICON(c.referrer_app)} {c.referrer_app || "direct"}
                    </span>
                    {c.country_code && (
                      <span className="text-[#71717A] flex items-center gap-1">
                        · <span>{flagEmoji(c.country_code)}</span>
                        <span className="truncate max-w-[70px]">{c.city || c.country_code}</span>
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[#71717A] flex-shrink-0">{format(new Date(c.ts), "HH:mm:ss")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
